/**
 * user-admin.js
 */

import React from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import { 
  useMediaQuery,
  Container,
  Box,
  Divider,
  Typography,
  Button,
  Link,
  Tooltip,
  Alert,
  IconButton,
  List,
  ListItem,
} from '@mui/material';
import { useForm, useFormContext, useWatch, useFieldArray, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parseISO, format, differenceInDays, addDays, addMonths, addYears } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { 
  isValidEmail,
  firstNameValidationRules,
  lastNameValidationRules,
  emailValidationRules,
  departmentNameValidationRules,
  phoneValidationRules,
} from 'utils/user-profile-validation';
import { UserContext } from 'contexts/user/user-context';
import { AppUtilityContext } from 'contexts/app-utility/app-utility-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import { SubscriptionType, SubscriptionStatus } from 'routing/routes/config/billing/billing-constants';
import PageTitle from 'components/page-title';
import FeatureBox from 'components/feature-box';
import UserSearch from 'components/user-search';
import Icon from 'components/icon';
import Card from 'components/card';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import TextField from 'components/form-inputs/text-field';
import CheckboxList from 'components/form-inputs/checkbox-list';
import Select from 'components/form-inputs/select';
import ChangePasswordFields from 'components/change-password-fields';
import Dialog from 'components/dialog';
import DropMenu from 'components/drop-menu';
import DateTimePicker from 'components/form-inputs/date-time-picker';


const STRIPE_BASE = `https://dashboard.stripe.com${process.env.REACT_APP_ENV === 'development' ? '/test' : ''}`;
const PAGE_TITLE = 'User Admin';
const BASE_PATH = '/admin/user';
const TRANSFER_SUB_FROM_FORM_NAME = 'transferSubcriptionFromUserId';
const BYPASS_OPTIONS = [
  { id: '', name: 'No Change' },
  { id: 0, name: 'No Bypass' },
  { id: 1, name: '1 Day' },
  { id: 3, name: '3 Days' },
  { id: 7, name: '7 Days' },
  { id: 14, name: '14 Days' },
  { id: 30, name: '30 Days' },
  { id: 60, name: '60 Days' },
  { id: 90, name: '90 Days' },
  { id: -1, name: 'Forever' },
];
const INITIAL_SUBSCRIPTION_TYPES = {
  NONE: '',
  TRIAL: 1,
  YEARLY_INVOICE: 2,
}
const INITIAL_SUBSCRIPTION_OPTIONS = [
  { id: INITIAL_SUBSCRIPTION_TYPES.NONE, name: 'None' },
  { id: INITIAL_SUBSCRIPTION_TYPES.TRIAL, name: 'Free Trial' },
  { id: INITIAL_SUBSCRIPTION_TYPES.YEARLY_INVOICE, name: 'Yearly Invoice' }
];
const SNACKBAR_ERROR_PARAMS = { 
  variant: 'error',
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'center',
  }
};
const EXTRA_PARAMS = {
  newPassword: '',
  confirmPassword: '',
  initialHandlerSubscription: '',
  nextBillingDate: null,
  updateSubscriptionBypassDays: '',
};
const NEW_USER = {
  id: 0,
  name: '',
  firstName: '',
  lastName: '',
  email: '',
  departmentName: '',
  phone: '',
  badge: '',
  data: '{}',
  allRoles: [],
  subscription: {
    subscriptions: [],
    ccEmails: [],
  },
  ...EXTRA_PARAMS
};


// Format incoming JSON data to add extra params and format any required structure.
const formatIncomingData = (data) => {
  return {
    ...data,
    ...EXTRA_PARAMS,
    
    subscription: {
      ...data.subscription,
      // format ccEmails into email-object structure required for react-hook-form
      ccEmails: data.subscription.ccEmails.map((email, i) => ({ id: i, email }))
    }
  };
}


// Format outgoing JSON data for required structure.
const formatOutgoingData = (data) => {
  return {
    ...data,
    subscription: {
      ...data.subscription,
      // format ccEmails into flat list of email strings
      ccEmails: data.subscription.ccEmails.map((emailObj) => emailObj.email)
    }
  };
}


const BillingDatePicker = (props) => {
  const { subscription, label, disabledMsg: disabledMsgProp } = props;

  let disabledMsg = disabledMsgProp;
  if (subscription && !disabledMsg) {
    if (subscription.balance !== 0) {
      disabledMsg = "The billing date cannot be changed when there is a balance owing";
    } else if (subscription.status.id !== SubscriptionStatus.ACTIVE) {
      disabledMsg = "The billing date can only be changed for active subscriptions";
    } else if (subscription.hasPendingInvoiceItems) {
      disabledMsg = "The billing date cannot be changed when the customer has pending invoice items";
    }
  }

  let minBillingDate, maxBillingDate;
  if (!disabledMsg) {
    const now = new Date();
    // allowed range is from today to 1 day less than the period length
    minBillingDate = now;
    maxBillingDate = addDays(
      subscription?.type.id === SubscriptionType.MONTHLY
      ? addMonths(now, 1)
      : addYears(now, 1),
      -1)
  }


  return (
    <Tooltip
      arrow
      placement='top'
      title={disabledMsg}
    >
      <span>
        <DateTimePicker
          variant='date'
          name='nextBillingDate'
          label={label}
          format="MM/dd/yyyy"
          closeOnSelect={true}
          clearable
          showTodayButton
          minDate={minBillingDate}
          maxDate={maxBillingDate}
          disabled={!!disabledMsg}
          shouldDisableDate={day => {
            const dayString = day.toDateString();
            // existing subs can't be reset to the same billing date, and new subs can't 
            // have the billing date set to today because that's the default.
            return subscription
              ? parseISO(subscription.paidToDate).toDateString() === dayString
              : new Date().toDateString() === dayString;
          }}
        />
      </span>
    </Tooltip>
  );
};


const HandlerSubscription = (props) => {
  const { editUser, setEditUser } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();
  const { control, setValue } = useFormContext();
  const handlerRole = editUser.allRoles.find(r => r.id === Types.Role.HANDLER);
  const handlerSubscription = editUser.subscription.subscriptions.find(s => s.role.id === Types.Role.HANDLER);
  const selectedInitialSubscription = useWatch({ control, name: 'initialHandlerSubscription' });



  // transfer subscription params
  const [isTransferOpen, setIsTransferOpen] = React.useState(false);
  const [isTransferLoading, setIsTransferLoading] = React.useState(false);
  const [transferSourceUser, setTransferSourceUser] = React.useState();
  const [transferUserError, setTransferUserError] = React.useState();
  const transferSourceSubscription = transferSourceUser?.subscription.subscriptions.find(s => s.role.id === Types.Role.HANDLER);

  // bypass params
  const bypassDays = editUser.subscription.bypassExpiryDate 
    ? differenceInDays(editUser.subscription.bypassExpiryDate, new Date())
    : null;
  const bypassValue = !bypassDays
    ? 'None'
    : bypassDays > 9999 
      ? 'Forever' 
      : bypassDays < 0
        ? `${bypassDays} day${bypassDays === -1 ? '' : 's'} ago`
        : `${bypassDays} more day${bypassDays === 1 ? '' : 's'}`;


  const getSubscriptionSummary = (subscription) => {
    let summary = `${subscription.status.name} ${subscription.type.name}`;
    if (subscription.type.id !== SubscriptionType.TRIAL) {
      summary += ` ${subscription.paymentType.name}`;
    }
    return summary;
  }


  const onSelectTransferUser = async (sourceUser) => {
    setTransferSourceUser();
    setTransferUserError();
    if (!sourceUser) {
      setValue(TRANSFER_SUB_FROM_FORM_NAME, null);
    } else if (sourceUser.id === editUser.id) {
      setTransferUserError('Cannot transfer from the same user.');
      return false;
    } else {
      try {
        setIsTransferLoading(true);
        const response = await axios.get(`/admin/user/${sourceUser.id}`);
        if (response.isSuccess) {
          const sourceHandlerSub = response.data.subscription.subscriptions.find(s => s.role.id === Types.Role.HANDLER);
          if (response.data.subscription.isBypassed) {
            setTransferUserError(`${sourceUser.name}'s subscription is bypassed.`);
            return false;
          } else if (!sourceHandlerSub) {
            setTransferUserError(`${sourceUser.name} has no handler subscription.`);
            return false;
          } else if (sourceHandlerSub.type.id === SubscriptionType.TRIAL) {
            setTransferUserError(`${sourceUser.name}'s handler subscription is a trial.`);
            return false;
          } else if (sourceHandlerSub.status.id !== SubscriptionStatus.ACTIVE) {
            setTransferUserError(`${sourceUser.name}'s handler subscription is not active and cannot be transferred.`);
            return false;
          } else if (sourceHandlerSub.balance > 0) {
            setTransferUserError(`${sourceUser.name}'s handler subscription has a balance of $${sourceHandlerSub.balance} and cannot be transferred.`);
            return false;
          }  else if (!sourceHandlerSub?.paidToDate) {
            setTransferUserError(`${sourceUser.name}'s handler subscription does not have a known paid-to date.`);
            return false;
          } else if (handlerSubscription && parseISO(sourceHandlerSub.paidToDate) < parseISO(handlerSubscription.paidToDate)) {
            const sourceDate = format(parseISO(sourceHandlerSub.paidToDate), 'MMM dd, yyyy');
            const currDate = format(parseISO(handlerSubscription.paidToDate), 'MMM dd, yyyy');
            setTransferUserError(`${sourceUser.name}'s handler subscription expires on ${sourceDate} which is sooner than the current user's handler subscription expiry of ${currDate}`);
            return false;
          } else if (sourceHandlerSub?.hasPendingInvoiceItems) {
            setTransferUserError(`${sourceUser.name}'s handler subscription cannot be transferred because ${sourceUser.name} has pending invoice items.`);
            return false;
          } else {
            setTransferSourceUser(response.data);
            setValue(TRANSFER_SUB_FROM_FORM_NAME, response.data.id);
          }
        }
      } finally {
        setIsTransferLoading(false);
      }
    }
  }


  const onCloseTransfer = () => {
    setIsTransferOpen(false);
    setIsTransferLoading(false);
    setTransferUserError();
    setTransferSourceUser();
  }


  const onPerformTransfer = async () => {
    try {
      setIsTransferLoading(true);
      const response = await axios.post(
        `/admin/user/${editUser.id}/subscription/transfer/${transferSourceUser.id}`,
        null,
        { bypassErrorHandling: true }
      );
      if (response.isSuccess) {
        enqueueSnackbar('Successfully transferred subscription', { variant: 'success' });
        setEditUser(response.data);
        onCloseTransfer();
      }
    } catch (e) {
      setTransferUserError(`${e.message ?? 'Unknown error transferring subscription'}`);
    } finally {
      setIsTransferLoading(false);
    }
  }


  return (
    <>
      <Card titleStyle='dark' title='Handler Subscription' sx={{ mt: 1 }}>
        <Box sx={{ p: 2, pt: 3, pb: 0 }}>

          {!handlerSubscription &&
          <>
            <Box sx={{
              mt: 1,
              mb: 2,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
            }}>
              <Typography variant='subtitle1' sx={{ }}>Subscription Type:</Typography>
              <Select
                name='initialHandlerSubscription'
                label='Subscription Type'
                displayEmpty
                options={INITIAL_SUBSCRIPTION_OPTIONS}
                sx={{ 
                  width: 180, 
                  ml: isMobile ? 0 : 2,
                  mt: isMobile ? 2 : 0, 
                }}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              {
                <BillingDatePicker
                  label='Custom Billing Date'
                  disabledMsg={selectedInitialSubscription === INITIAL_SUBSCRIPTION_TYPES.YEARLY_INVOICE
                    ? undefined
                    : "An initial billing date can only be set for yearly invoice subscriptions"
                  }
                />
              }
            </Box>
          </>}

          {handlerSubscription &&
          <>
            {/* SUBSCRIPTION */}
            <Typography variant='subtitle1' sx={{ mb: 3 }}>
              Info: <b>{handlerSubscription
                ? getSubscriptionSummary(handlerSubscription)
                : 'None'}</b>
            </Typography>

            {/* NEXT BILLING DATE */}
            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle1' sx={{ mb: 2 }}>
                Next Billing Date: <b>{handlerSubscription.paidToDate ? parseISO(handlerSubscription.paidToDate).toDateString() : 'None'}</b>
              </Typography>
              {handlerSubscription &&
                <>
                  <BillingDatePicker
                    label='Change Billing Date'
                    subscription={handlerSubscription}
                  />
                  {handlerSubscription.type.id === SubscriptionType.TRIAL &&
                    <Typography variant='subtext-small' sx={{ mt: 0.5 }}>
                      Immediately changes subscription to yearly invoice
                    </Typography>
                  }
                </>
              }      
            </Box>
            
            {/* CUSTOMER */}
            <Typography variant='subtitle1' sx={{ mb: 3 }}>
              Customer: <b>{editUser.subscription.customerId 
                ? <Link 
                  href={`${STRIPE_BASE}/customers/${editUser.subscription.customerId}`} 
                  target='_blank'
                  children={editUser.subscription.customerId}
                />
                : 'None'}</b>
            </Typography>
            <Typography variant='subtitle1' sx={{ mb: 3 }}>
              Subscription: <b>{handlerSubscription.subscriptionId
                ? <Link
                  href={`${STRIPE_BASE}/subscriptions/${handlerSubscription.subscriptionId}`}
                  target='_blank'
                  children={handlerSubscription.subscriptionId}
                />
                : 'None'}</b>
            </Typography>
          </>}

          {/* BYPASS */}
          <Box sx={{ 
            mb: 3,
            display: 'flex', 
            flexDirection: 'column',
          }}>
            <Typography variant='subtitle1' sx={{ mb: 2 }}>Current Bypass: <b>{bypassValue}</b></Typography>
            <Select 
              name='updateSubscriptionBypassDays' 
              label='Update Bypass' 
              options={BYPASS_OPTIONS} 
              displayEmpty
              sx={{ width: 150 }}
            />
          </Box>

          {/* TRANSFER-SUBSCRIPTION */}
          <Tooltip
            arrow
            placement='top'
            title={editUser.id === 0 
              ? 'You must create a user before you can transfer a subscription to them.' 
              : handlerRole?.status.isDeactivated
                ? 'You cannot transfer from another subscription if this user is deactivated'
                : handlerSubscription?.balance > 0
                  ? 'You cannot transfer from another subscription if this user has an unpaid balance'
                  : (handlerSubscription && !handlerSubscription.paidToDate)
                    ? `This user's handler subscription does not have a known paid-to date`
                    : ''}
          >
            <Box sx={{ display: 'inline-block' }}>
              <Button
                variant='outlined'
                children='Transfer Subscription From ...'
                disabled={
                  editUser.id === 0 
                  || handlerRole?.status.isDeactivated
                  || handlerSubscription?.balance > 0
                  || (handlerSubscription && !handlerSubscription.paidToDate)
                }
                onClick={() => setIsTransferOpen(true)}
                sx={{ mb: 3 }}
              />
            </Box>
          </Tooltip>

        </Box>
      </Card>

      <Dialog
        title='Transfer Subscription'
        maxWidth='xs'
        fullWidth
        open={isTransferOpen}
        okLabel='Perform Transfer'
        okDisabled={!transferSourceUser}
        onOk={onPerformTransfer}
        onClose={onCloseTransfer}
      >
        <FeatureBox loading={isTransferLoading}>
          <Box sx={{
            mt: 1,
            display: 'flex',
          }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ mb: 3 }}>
                Transfer a subscription from another user (the source) to this user. This is only possible if 
                neither user has an amount owing and if the current user's subscription expires before the source 
                user's subscription. During the process both subscriptions are cancelled and a new subscription 
                is created for the current user. The subscription will be of the same type as the source subscription 
                and the first billing date will be set to the source subscription's paid-to date, effectively creating 
                a free period up to that date.
              </Typography>
              <UserSearch 
                role={Types.Role.HANDLER} 
                label='Transfer Subscription From Handler'
                sx={{ flex: 1 }} 
                value={transferSourceUser} 
                onSelect={async (value) => {
                  return await onSelectTransferUser(value);
                }} 
              />
              {transferSourceSubscription &&
              <Typography sx={{ mt: 1 }}>
                {getSubscriptionSummary(transferSourceSubscription)},&nbsp;
                Paid until {format(parseISO(transferSourceSubscription.paidToDate), 'MMM dd, yyyy')}
              </Typography>}
              {transferUserError && <Alert severity='error' sx={{ mt: 2 }}>{transferUserError}</Alert>}
            </Box>
          </Box>
        </FeatureBox>
      </Dialog>
    </>
  )
}


const CCEmails = (props) => {
  const { control } = useFormContext();
  const { fields: ccEmails, append, remove } = useFieldArray({ control, name: 'subscription.ccEmails' });


  return ccEmails
    ? (
      <Card 
        titleStyle='dark' 
        title='Billing CC Emails'
        rightHeaderActions={(
          <Button
            color='inherit'
            startIcon={<Icon type='add' />}
            children='Add Email'
            onClick={() => append({ email: '' })}
          />
        )}
        sx={{ mt: 4 }}
      >
        {ccEmails.length > 0 &&
        <List>
          {ccEmails.map((email, i) => (
            <ListItem 
              key={i} 
              secondaryAction={(
                <IconButton
                  onClick={() => remove(i)}
                  children={<Icon type='delete' />}
                />
              )}
              children={(
                <TextField 
                  name={`subscription.ccEmails[${i}].email`} 
                  sx={{ width: 'calc(100% - 24px)' }}
                  placeholder='Email'
                  rules={{
                    validate: (val) => isValidEmail(val) || 'Please enter a valid email'
                  }}
                />
              )}
            />
          ))}
        </List>}
      </Card>
    )
    : null;
}



export default function UserAdmin() {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { t } = useTranslation('general');
  const history = useHistory();
  const { userId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { admin, impersonateUser } = React.useContext(UserContext);
  const { confirm } = React.useContext(AppUtilityContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [editUser, setEditUser] = React.useState();
  const roleOptions = Types.Role.entries(t, [Types.Role.MANAGER]);
  
  const formContext = useForm();
  const { control, reset, handleSubmit, formState } = formContext;
  const roles = useWatch({ control, name: 'allRoles' });
  const isDeactivated = !Boolean(editUser?.allRoles.find(r => !r.status.isDeactivated));


  const getBillingUser = React.useCallback(async (userId) => {
    if (!userId) {
      setEditUser();
      history.push(BASE_PATH);
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.get(`/admin/user/${userId}`);
      if (response.isSuccess) {
        const responseUser = formatIncomingData(response.data);
        setEditUser(responseUser);
        reset({ ...responseUser, nextBillingDate: null });
        history.push(`${BASE_PATH}/${response.data.id}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [reset, history]);


  React.useEffect(() => {
    if (userId && !editUser) {
      getBillingUser(userId);
    }
  }, [userId, editUser, getBillingUser]);


  const onSubmit = React.useCallback(async (data) => {
    try {
      setIsLoading(true);
      data = formatOutgoingData(data);
      const method = data.id === 0 ? 'post' : 'put';
      const url = `/admin/user${data.id > 0 ? `/${data.id}` : ''}`;
      const response = await axios({ method, url, data, bypassErrorHandling: true });
      if (response.isSuccess) {
        enqueueSnackbar(`User ${data.id > 0 ? 'updated' : 'created'}`, { variant: 'success' });
        const updatedUser = formatIncomingData(response.data);
        setEditUser(updatedUser);
        reset(updatedUser);
        if (data.id === 0) {
          history.push(`${BASE_PATH}/${updatedUser.id}`);
        }
      } else {
        throw new Error(response.statusText);
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        enqueueSnackbar(e.message, SNACKBAR_ERROR_PARAMS);
      } else {
        let errorMessage = `Error ${data.id > 0 ? 'updating' : 'creating'} user`;
        if (e.message) {
          errorMessage += `: ${e.message}`;
        }
        enqueueSnackbar(errorMessage, SNACKBAR_ERROR_PARAMS);
      }
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, reset, history]);


  const saveButton = React.useMemo(() => (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty}
      children='Save'
      onClick={() => handleSubmit(onSubmit)()}
    />
  ), [formState.isDirty, handleSubmit, onSubmit]);


  const cancelButton = React.useMemo(() => (
    <DrawerActionButton
      disabled={!formState.isDirty && editUser?.id !== 0}
      children='Cancel'
      onClick={() => {
        if (!editUser || editUser?.id === 0) {
          history.push(BASE_PATH);
          setEditUser();
        } else {
          reset(editUser);
        }
      }}
      sx={{ mr: 2 }}
    />
  ), [formState.isDirty, editUser, reset, history]);


  const impersonateButton = React.useMemo(() => (
    <Button
      variant='outlined'
      children='Impersonate'
      startIcon={<Icon type='impersonate' />}
      sx={{ mr: 2 }}
      onClick={async () => {
        if (await impersonateUser(editUser.id)) {
          window.location = '/';
        }
      }}
    />
  ), [editUser?.id, impersonateUser]);


  const menu = React.useMemo(() => {
    const onDeactivate = async () => {
      if (await confirm({
        title: 'Confirm Deactivate',
        titleColor: 'warning.main',
        text: <>
          Are you sure you want to deactivate this user? <br/><br/>
          Their Handler subscription will be canceled and all roles will be deactivated.
        </>
      })) {
        try {
          setIsLoading(true);
          const response = await axios.post(`/admin/user/${editUser.id}/deactivate`);
          if (response.isSuccess) {
            enqueueSnackbar(`User deactivated`, { variant: 'success' });
            const updatedUser = {
              ...response.data,
              ...EXTRA_PARAMS
            };
            setEditUser(updatedUser);
            reset(updatedUser);
          }
        } catch (e) {
          enqueueSnackbar(`Error deactivating user`, SNACKBAR_ERROR_PARAMS);
        } finally {
          setIsLoading(false);
        }
      }
    }


    const onReactivate = async () => {
      if (await confirm({
        title: 'Confirm Reactivate',
        titleColor: 'success.main',
        text: <>
          Are you sure you want to reactivate this user? <br/><br/>
          Their Handler role will be set to <b>Limited</b> and all other roles will be <b>Active</b>.
        </>
      })) {
        try {
          setIsLoading(true);
          const response = await axios.post(`/admin/user/${editUser.id}/reactivate`);
          if (response.isSuccess) {
            enqueueSnackbar(`User reactivated`, { variant: 'success' });
            const updatedUser = {
              ...response.data,
              ...EXTRA_PARAMS
            };
            setEditUser();
            reset(updatedUser);
          }
        } catch (e) {
          enqueueSnackbar(`Error reactivating user`, SNACKBAR_ERROR_PARAMS);
        } finally {
          setIsLoading(false);
        }
      }
    }


    const onDelete = async () => {
      if (await confirm({
        title: 'Confirm Delete',
        titleColor: 'error.main',
        text: <>
          This will permanently remove the user and all their records. Are you sure you want to proceed?<br/><br/>
          <b>WARNING: THIS IS ONLY FOR DELETING TEST ACCOUNTS!</b><br/><br/>
          If this user created events and exercises then other users with associated completions will lose their records.<br/><br/>
          Also note that this only cancels the associated subscription. Customers can be removed from Stripe but subscriptions cannot be completely deleted.
        </>
      })) {
        try {
          setIsLoading(true);
          const response = await axios.delete(`/admin/user/${editUser.id}`);
          if (response.isSuccess) {
            enqueueSnackbar(`User deleted`, { variant: 'success' });
            setEditUser();
            history.push(BASE_PATH);
          }
        } catch (e) {
          enqueueSnackbar(`Error deleting user`, SNACKBAR_ERROR_PARAMS);
        } finally {
          setIsLoading(false);
        }
      }
    }

    return (
      <DropMenu
        id='user-admin-extra-actions'
        items={[{
          key: 'old-editor',
          icon: 'open-in-tab',
          name: 'Open in old admin editor',
          onSelect: () => window.open(`${process.env.REACT_APP_ADMIN_HOST}/Home/Edit/${editUser.id}`)
        }, ...([isDeactivated 
          ? {
            key: 'reactivate',
            icon: 'check-all',
            name: 'Reactivate',
            onSelect: onReactivate,
          }
          : {
            key: 'deactivate',
            icon: 'lock',
            name: 'Deactivate',
            onSelect: onDeactivate,
          }
        ]), {
          key: 'delete',
          icon: 'delete',
          name: 'Delete',
          onSelect: onDelete,
        }]}
        label={(
          <IconButton
            sx={{ backgroundColor: 'sys.surfaceContainer' }}
            data-testid='user-admin-extra-actions-button'
          >
            <Icon type='menu' />
          </IconButton>
        )}
      />
    )
  }, [editUser?.id, isDeactivated, confirm, enqueueSnackbar, history, reset]);                  
  

  React.useEffect(() => {
    document.title = `PACKTRACK - ${PAGE_TITLE}`;
    setAppBar(PAGE_TITLE, [cancelButton, saveButton]);
  }, [setAppBar, cancelButton, saveButton]);
  

  if (!admin) {
    return null;
  }


  return (
    <Container sx={{ py: isMobile ? 3 : 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        {!isMobile && <PageTitle iconName='user-admin' title={PAGE_TITLE} />}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <UserSearch 
            value={editUser?.id > 0 ? editUser : null} 
            onSelect={(resultUser) => {
              getBillingUser(resultUser?.id);
            }} 
            sx={{ width: '100%', minWidth: isMobile ? null : 300, maxWidth: isMobile ? null : 300 }} 
          />
          <Button
            variant='contained'
            color='secondary'
            children='Create User'
            disabled={formState.isDirty}
            sx={{ ml: 2, whiteSpace: 'nowrap', textTransform: 'none' }}
            onClick={() => {
              setEditUser(NEW_USER);
              reset(NEW_USER);
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: isMobile ? 2 : 3 }} />

      <FeatureBox loading={isLoading} spinnerSize={50} sx={{ minHeight: 200 }}>
        {editUser &&
        <FormProvider {...formContext}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            {isMobile && editUser.id > 0 &&
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
              {impersonateButton}
              {menu}
            </Box>}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, maxWidth: 550 }}>

                {/* NAME */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  '& .MuiFormControl-root:not(:first-of-type)': {
                    ml: isMobile ? 0 : 3,
                    mt: isMobile ? 3 : 0,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 'sizes.5'
                  },
                }}>
                  <TextField fullWidth label='First Name' name='firstName' rules={firstNameValidationRules()} />
                  <TextField fullWidth label='Last Name' name='lastName' rules={lastNameValidationRules()} />
                </Box>

                {/* EMAIL */}
                <TextField 
                  fullWidth 
                  label='Email' 
                  name='email' 
                  sx={{ mt: 3 }} 
                  setValueAs={(val) => val.trim()}
                  rules={emailValidationRules()} 
                />

                {/* DEPT NAME */}
                <TextField 
                  fullWidth 
                  label='Department Name' 
                  name='departmentName' 
                  sx={{ mt: 3 }} 
                  rules={departmentNameValidationRules()} 
                />

                {/* PHONE */}
                <TextField 
                  fullWidth 
                  label='Phone' 
                  name='phone' 
                  sx={{ mt: 3 }} 
                  rules={phoneValidationRules()} 
                />

                {/* BADGE */}
                <TextField 
                  fullWidth 
                  label='Badge' 
                  name='badge' 
                  sx={{ mt: 3 }} 
                />                

                {/* ROLES */}
                <Box sx={{ 
                  mt: 4, 
                  pl: 0.5, 
                  '& .MuiGrid-container': { flexDirection: 'column' },
                  '& .MuiFormControl-root': { width: '100%' },
                }}>
                  <CheckboxList
                    name={`allRoles`}
                    label='Roles'
                    options={roleOptions}
                    renderOption={(option) => {
                      const userRole = editUser?.allRoles?.find(ur => ur.id === option.id);
                      return <>{option.name}{userRole?.status 
                        ? <>&nbsp;&nbsp;&nbsp;<Typography variant='caption' sx={{ color: 'sys.onSurfaceVariant' }}>({userRole.status.name})</Typography></> 
                        : ''}</>;
                    }}
                    rules={{
                      validate: (value) => value.length > 0 || 'Please select at least one role'
                    }}
                  />
                </Box>

                {/* HANDLER SUBSCRIPTION */}
                {roles.find(r => r.id === Types.Role.HANDLER) &&
                <HandlerSubscription editUser={editUser} setEditUser={setEditUser} />}

                {/* CC EMAILS */}
                <CCEmails />
                
                {/* PASSWORD */}
                <Box sx={{ mt: 5 }}>
                  {editUser.id === 0 &&
                  <Typography sx={{ mb: 2 }}>
                    Adding a password is OPTIONAL. If you don't provide one then the onboarding email will include a link for the user to set their own password.
                  </Typography>}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    '& .MuiGrid-item': {
                      flex: 1,
                      '& .MuiFormControl-root': {
                        width: '100%',
                      }
                    },
                    '& .MuiGrid-item:not(:first-of-type)': {
                      ml: isMobile ? 0 : 3,
                      mt: isMobile ? 3 : 0,
                    }
                  }}>
                    <ChangePasswordFields allowEmpty />
                  </Box>
                </Box>

                {/* LAST WEB LOGIN */}
                {editUser.lastWebLogin &&
                <Typography variant='subtitle2' sx={{ mt: 3 }}>
                  Last Login: {parseISO(editUser.lastWebLogin).toDateString()}, {parseISO(editUser.lastWebLogin).toTimeString()}
                </Typography>}

              </Box>

              {!isMobile &&
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box>
                  {cancelButton}
                  {saveButton}
                </Box>
                {editUser.id > 0 &&
                <Box sx={{ mt: 2 }}>
                  {impersonateButton}
                  {menu}
                </Box>}
              </Box>}
            </Box>

          </form>
        </FormProvider>}
      </FeatureBox>
    </Container>
  )
}